/**
 * Resilience-readiness hooks (P267.4).
 *
 * The live suggested read (`/v1/readiness`) + the persisted self-assessment
 * lifecycle (`/v1/readiness/assessment`).
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { api, currentAuthToken, type ApiError } from "shared/api/client";
import type {
  BaselCrosswalk,
  MaturityAssessment,
  Readiness,
  ResilienceScore,
} from "shared/api/types";

export const readinessKeys = {
  read: ["readiness"] as const,
  assessment: ["readiness", "assessment"] as const,
  baselCrosswalk: ["readiness", "basel-crosswalk"] as const,
  score: ["readiness", "score"] as const,
};

/** P335 -- the MARSAD Resilience Score (live-computed). */
/*
 * Round 9 E9 -- `enabled`, because these three were firing behind a
 * card that then hid itself.
 *
 * ReadinessCard already called `canReadReadiness` and rendered null for
 * a preparer. A hook cannot be conditional, so the queries fired first
 * and the 403s arrived on every home load, eighth round running. The
 * gate has to reach the QUERY, not only the render -- authority.ts says
 * as much in its own comment ("decline to ASK when they know the
 * answer") and the card was not taking it up.
 */
export function useResilienceScore(
  applies = true,
): UseQueryResult<ResilienceScore, ApiError> {
  return useQuery({
    queryKey: readinessKeys.score,
    queryFn: () => api.get<ResilienceScore>("/v1/readiness/score"),
    enabled: applies,
  });
}

export function useReadiness(applies = true): UseQueryResult<Readiness, ApiError> {
  return useQuery({
    queryKey: readinessKeys.read,
    queryFn: () => api.get<Readiness>("/v1/readiness"),
    enabled: applies,
  });
}

/** P330 -- the Basel-7 op-res readiness crosswalk (lens over Readiness). */
export function useBaselCrosswalk(): UseQueryResult<BaselCrosswalk, ApiError> {
  return useQuery({
    queryKey: readinessKeys.baselCrosswalk,
    queryFn: () => api.get<BaselCrosswalk>("/v1/readiness/basel-crosswalk"),
  });
}

export function useMaturityAssessment(
  applies = true,
): UseQueryResult<MaturityAssessment | null, ApiError> {
  return useQuery({
    queryKey: readinessKeys.assessment,
    queryFn: () => api.get<MaturityAssessment | null>("/v1/readiness/assessment"),
    enabled: applies,
  });
}

export function useStartAssessment(): UseMutationResult<
  MaturityAssessment,
  ApiError,
  void
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<MaturityAssessment>("/v1/readiness/assessment"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: readinessKeys.assessment });
    },
  });
}

export function useSetDimension(): UseMutationResult<
  MaturityAssessment,
  ApiError,
  { code: string; score: number; narrative?: string | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ code, score, narrative }) =>
      api.patch<MaturityAssessment>(`/v1/readiness/assessment/dimensions/${code}`, {
        score,
        narrative: narrative ?? null,
      }),
    onSuccess: (data) => {
      qc.setQueryData(readinessKeys.assessment, data);
    },
  });
}

/** Fetch the signed maturity pack as a blob + trigger a browser download. */
export async function downloadReadinessPack(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch("/v1/readiness/pack.zip", {
    method: "GET",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(response.statusText || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-readiness.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** P335 -- download the signed MARSAD Resilience Score pack. */
export async function downloadScorePack(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch("/v1/readiness/score/pack.zip", {
    method: "GET",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(response.statusText || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-resilience-score.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/** P330 -- download the signed Basel-7 readiness pack (examiner-ready). */
export async function downloadBaselCrosswalkPack(): Promise<void> {
  const token = currentAuthToken();
  const response = await fetch("/v1/readiness/basel-crosswalk/pack.zip", {
    method: "GET",
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!response.ok) {
    throw new Error(response.statusText || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-basel-crosswalk.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

export function useSignoff(): UseMutationResult<
  MaturityAssessment,
  ApiError,
  // B3 -- the signer is the session. A name and email in the body
  // let one person record a signature under another's name.
  // C2 -- and the reason. The endpoint has accepted a note all along;
  // the panel never offered a box, so the board signature on the
  // maturity assessment carried no reason at all.
  { role: "cro" | "ops_res"; note?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    /* Round 11 D5 -- the caller passes its own `onError` to `mutate`,
       which says "I will handle this" and which react-query cannot
       see: a per-call callback lives on the observer, not in
       `mutation.options`, so the global failure sink announced the
       server's detail as well. One failure, two wordings -- the round
       8 E1 defect, through the door its ratchet was not watching.
       Measured before marking: every flagged call site's onError does
       show the failure, so suppressing the sink silences nothing. */
    meta: { handled: true },
    mutationFn: (body) =>
      api.post<MaturityAssessment>("/v1/readiness/assessment/signoff", body),
    onSuccess: (data) => {
      qc.setQueryData(readinessKeys.assessment, data);
    },
  });
}
